
import Vue from 'vue';
import Map from '@/components/Map.vue';
import Loader from '@/components/Loader.vue';
import YslabLogo from '@/components/YslabLogo.vue';
import Compass from '@/components/Compass.vue';
import ErrorPage from '@/components/ErrorPage.vue';
import RotateTooltip from '@/components/RotateTooltip.vue';
import NotSupportedBrowser from '@/components/NotSupportedBrowser.vue';
import checkBrowserSupport, { detectWebpSupport, updateWebpSupport } from '@/helpers/supportedBrowsers';
import { osName, isMobile } from '@/helpers/mobile-device-detect';
import AppService from './services/AppService';
import RouteService from './services/RouteService';
import { Breakpoint } from './models/app.model';
import { TextureData } from './models/viewer.model';

export default Vue.extend({
    name: 'App',
    data() {
        return {
            AppService,
            appService: null as null | AppService,
            showMap: window.innerWidth >= 768,
            activePointViewAreaRotateDeg: 0,
            appConfigLoaded: false,
            defaultImageLoaded: false,
            loadedImages: [] as Array<string>,
            loadedTextures: [] as Array<string>,
            loadingTextures: [] as Array<string>,
            activeFloor: 0,
            viewerIsReady: false,
            initialViewerIsReady: false,
            classListWithUserDevice: `--${osName} ${isMobile ? '--mobile' : ''}`,
            appSizeBreakpoint: 'desktop' as Breakpoint,
            defaultRoomLoadingProgress: 0,
            wasMapPointClicked: false,
            isSupported: false,
            supportedBrowserChecked: false,
            preloadingViewError: false,
            resimoDataLoadError: false,
            apartmentNotFoundError: false,
            panoramaMappingsLoadError: false,
            markersConfigLoadError: false,
        };
    },
    computed: {
        errorVisible(): boolean {
            return this.preloadingViewError || this.resimoDataLoadError || this.apartmentNotFoundError
                || this.panoramaMappingsLoadError || this.markersConfigLoadError;
        },
        configErrorVisible(): boolean {
            if (this.resimoDataLoadError || this.panoramaMappingsLoadError) return true;
            if (this.apartmentNotFoundError) return false;
            if (this.markersConfigLoadError) return true;
            if (this.preloadingViewError) return false;
            return false;
        },
    },
    components: {
        PhotoViewer: () => import(/* webpackChunkName: "p-h" */ '@/components/PhotoViewer.vue'),
        Map,
        Loader,
        YslabLogo,
        Compass,
        RotateTooltip,
        ErrorPage,
        NotSupportedBrowser,
    },
    methods: {
        toggleMap() {
            this.showMap = !this.showMap;
        },
        rotateActivePointViewArea(rotationDeg: number) {
            this.activePointViewAreaRotateDeg = rotationDeg;
        },
        async initAppService() {
            this.appService = new AppService(this.appSizeBreakpoint);

            await this.appService.setupApartmentService();
            await this.appService.setupMarkersConfig();
        },
        closeMapIfMobile() {
            if (window.innerWidth < 768) {
                this.showMap = false;
            }
        },
        textureLoaded(data: TextureData) {
            this.loadingTextures = this.loadingTextures
                .filter((texture) => texture !== data.name);
            this.loadedTextures.push(data.name);
            this.appService?.viewerService?.setRoomTexture(data.name, data.texture);

            if (data.name === this.appService?.viewerService?.getDefaultRoomName()) {
                this.defaultImageLoaded = true;
                this.appService.viewerService.loadMissingRooms();
            }
        },
        updatePageTitle(apartmentSymbol: string) {
            if (apartmentSymbol) {
                document.title = `${document.title} ${apartmentSymbol}`;
            }
        },
        getCompassRotateDeg() {
            if (!this.appService) return 0;
            return this.appService.getCompassRotateDegOffset()
                - this.activePointViewAreaRotateDeg;
        },
        setAppSizeBreakpoint() {
            const routeService = new RouteService();
            const appContainer = this.$refs.app as HTMLDivElement;
            let appWidth: number;

            if (routeService.targetWidth) {
                appWidth = routeService.targetWidth;
            } else {
                appWidth = appContainer.getBoundingClientRect().width;
            }

            if (appWidth <= 940) this.appSizeBreakpoint = 'mobile';
            else if (appWidth <= 1400) this.appSizeBreakpoint = 'laptop';
            else this.appSizeBreakpoint = 'desktop';
        },
        onMapPointClickHandler(roomName: string) {
            if (!this.loadedTextures.includes(roomName)) return;
            this.viewerIsReady = false;
            this.appService?.setActiveRoom(roomName);
            this.wasMapPointClicked = true;
            this.rotateActivePointViewArea(this.appService?.getViewAreaRotateDegOffset() || 0);
            this.closeMapIfMobile();
        },
        getLanguageFromUrl() {
            const queryString = window.location.search;
            const urlParams = new URLSearchParams(queryString);
            const lang = urlParams.get('lang');
            if (lang) this.$i18n.locale = lang;
        },
        onFloorChange(newFloor: number) {
            this.activeFloor = newFloor;
        },
    },
    watch: {
        viewerIsReady() {
            this.defaultImageLoaded = true;
            this.appService?.viewerService?.loadMissingRooms();

            if (!this.initialViewerIsReady) {
                this.initialViewerIsReady = true;
            }
        },
        configErrorVisible(val) {
            if (!val) return;
            if (!this.appService?.routeService.routeParams.wasAutoRefreshed) {
                const searchParams = new URLSearchParams(window.location.search);
                searchParams.set('wasAutoRefreshed', 'true');
                const newUrl = `${window.location.origin}${window.location.pathname}?${searchParams.toString()}`;
                window.history.pushState({ path: newUrl }, '', newUrl);
                window.location.reload();
            }
        },
    },
    async created() {
        this.isSupported = checkBrowserSupport();
        this.getLanguageFromUrl();
        const isWebpSupported = await detectWebpSupport();
        updateWebpSupport(isWebpSupported);
    },
    mounted() {
        this.supportedBrowserChecked = true;
        this.setAppSizeBreakpoint();

        this.initAppService().then(() => {
            if (this.appService?.markersConfig) {
                this.appConfigLoaded = true;
            }

            this.$watch('appService.viewerService.loadingData.defaultRoom.finished', (val) => {
                if (val) this.defaultImageLoaded = true;
            }, { deep: true, immediate: true });

            this.$watch('appService.viewerService.loadingData.defaultRoom.loadingProgress', (val) => {
                this.defaultRoomLoadingProgress = val;
            }, { deep: true, immediate: true });

            this.$watch('appService.apartmentService.apartment.name', (val) => {
                if (val) this.updatePageTitle(val);
            }, { deep: true, immediate: true });

            this.$watch('appService.viewerService.error', (val) => {
                if (val === true) this.preloadingViewError = true;
            }, { immediate: true });

            this.$watch('appService.resimoDataLoadError', (val) => {
                if (val === true) this.resimoDataLoadError = true;
            }, { immediate: true });

            this.$watch('appService.apartmentNotFoundError', (val) => {
                if (val === true) this.apartmentNotFoundError = true;
            }, { immediate: true });

            this.$watch('appService.panoramaMappingsLoadError', (val) => {
                if (val === true) this.panoramaMappingsLoadError = true;
            }, { immediate: true });

            this.$watch('appService.markersConfigLoadError', (val) => {
                if (val === true) this.markersConfigLoadError = true;
            }, { immediate: true });

            this.$watch('appService.viewerService.loadingData.loadedImages', (val) => {
                this.loadedImages = val;
            });
        });
    },
});
